import type { HouseholdFactFindDocument } from '@cf/shared';

export interface ReviewDraft {
  document: HouseholdFactFindDocument;
  expectedVersion: number | null;
}

// In-memory recovery survives SPA navigation, but not refresh or tab closure.
// Financial drafts are never written to browser storage. Scoped to authenticated firm + adviser + client. Never a
// source for autosave until the adviser explicitly restores it against its base.
const copies = new Map<string, string>();
export const hasReviewDrafts = (): boolean => copies.size > 0;
export const readRecoveryCopy = (key: string): string | null => copies.get(key) ?? null;
export function keepRecoveryCopy(key: string, value: string | null): void {
  if (value === null) copies.delete(key);
  else copies.set(key, value);
}

export function reviewDraftKey(firm: string, adviser: string, client: string): string {
  return `enzo-review-draft:${firm}:${adviser}:${client}`;
}

export function readReviewDraft(key: string): ReviewDraft | null {
  try {
    const value: unknown = JSON.parse(readRecoveryCopy(key) ?? 'null');
    if (value === null || typeof value !== 'object') return null;
    const draft = value as ReviewDraft;
    if (!draft.document || typeof draft.document !== 'object') return null;
    if (draft.expectedVersion !== null && !Number.isInteger(draft.expectedVersion)) return null;
    return draft;
  } catch {
    return null;
  }
}

export function writeReviewDraft(key: string, draft: ReviewDraft | null): boolean {
  try {
    if (draft === null) keepRecoveryCopy(key, null);
    else keepRecoveryCopy(key, JSON.stringify(draft));
    return true;
  } catch {
    return false;
  }
}

export function clearReviewDrafts(): void {
  copies.clear();
}
